import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardActions from "@mui/material/CardActions";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { toast } from "sonner";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import logo from "@/assets/jobyoda-logo.webp";
import { useAuth } from "@/hooks/useAuth";
import { useStripeCheckout } from "@/hooks/useStripeCheckout";
import { useSubscription } from "@/hooks/useSubscription";

export const Route = createFileRoute("/pricing")({
  component: PricingPage,
  head: () => ({
    meta: [
      { title: "Pricing — JobYoda" },
      {
        name: "description",
        content:
          "Every plan unlocks every feature. Pick the monthly volume that matches your job search — from a curious taste to fully autonomous applying.",
      },
      { property: "og:title", content: "Pricing — JobYoda" },
      {
        property: "og:description",
        content:
          "All features on every tier. Free, Starter, Pro, and Premium Agentic — pick the monthly volume that fits how hard you're searching.",
      },
    ],
  }),
});

type Tier = {
  name: string;
  productId?: "starter_plan" | "pro_plan" | "premium_agentic_plan";
  monthlyPriceId?: string;
  yearlyPriceId?: string;
  monthly: number;
  tagline: string;
  recommended?: boolean;
  cta: string;
  ctaVariant: "outlined" | "contained";
};

const TIERS: Tier[] = [
  {
    name: "Free",
    monthly: 0,
    tagline: "Taste every feature with starter quotas.",
    cta: "Get started",
    ctaVariant: "outlined",
  },
  {
    name: "Starter",
    productId: "starter_plan",
    monthlyPriceId: "starter_monthly",
    yearlyPriceId: "starter_yearly",
    monthly: 9,
    tagline: "Curious browsers running a few applications a week.",
    cta: "Start with Starter",
    ctaVariant: "outlined",
  },
  {
    name: "Pro",
    productId: "pro_plan",
    monthlyPriceId: "pro_monthly",
    yearlyPriceId: "pro_yearly",
    monthly: 19,
    tagline: "Active seekers running a real pipeline every week.",
    cta: "Upgrade to Pro",
    ctaVariant: "outlined",
  },
  {
    name: "Premium Agentic",
    productId: "premium_agentic_plan",
    monthlyPriceId: "premium_agentic_monthly",
    yearlyPriceId: "premium_agentic_yearly",
    monthly: 69,
    tagline: "Hand it off — unlimited everything, agent applies for you.",
    recommended: true,
    cta: "Go Premium",
    ctaVariant: "contained",
  },
];

type QuotaRow = {
  capability: string;
  hint?: string;
  values: [string, string, string, string]; // Free, Starter, Pro, Premium
};

const QUOTAS: { section: string; rows: QuotaRow[] }[] = [
  {
    section: "Discover & score",
    rows: [
      { capability: "Job descriptions saved", values: ["5", "25", "Unlimited", "Unlimited"] },
      { capability: "1–10 success scores", values: ["5", "50", "Unlimited", "Unlimited"] },
      { capability: "Career path maps (D3)", values: ["1 saved", "3", "10", "Unlimited"] },
    ],
  },
  {
    section: "Apply",
    rows: [
      { capability: "Tailored resumes", values: ["1", "5", "30", "Unlimited"] },
      { capability: "Cover letters", values: ["1", "5", "30", "Unlimited"] },
      { capability: "Application Q&A answers", values: ["3", "25", "150", "Unlimited"] },
      {
        capability: "Resume regenerations",
        hint: "Re-run on the same JD",
        values: ["0", "1", "3", "Unlimited"],
      },
      {
        capability: "Autonomous autofill runs",
        hint: "Agent submits for you",
        values: ["0", "0", "3 trial runs", "Unlimited"],
      },
    ],
  },
  {
    section: "Track & learn",
    rows: [
      { capability: "Forwarded-email tracking", values: ["10 emails", "50", "500", "Unlimited"] },
      { capability: "Rejection insights", values: ["3", "15", "Unlimited", "Unlimited"] },
      { capability: "Learning queue items", values: ["5", "25", "Unlimited", "Unlimited"] },
      {
        capability: "Profile rebuild from rejections",
        hint: "Auto-refactor your profile",
        values: ["Manual only", "Manual only", "1 / month", "Auto, unlimited"],
      },
    ],
  },
  {
    section: "Interview coaching",
    rows: [
      { capability: "Interview questions", values: ["2", "5", "30", "Unlimited"] },
      {
        capability: "Re-recordings per question",
        hint: "Retry the same answer",
        values: ["0", "1", "2", "Unlimited"],
      },
    ],
  },
];

const FAQS = [
  {
    q: "What happens when I hit a quota?",
    a: "You'll see a soft warning at 80% and a hard cap at 100%, with a one-click upgrade modal showing exactly which limit you hit and what the next tier gives. Quotas reset on the 1st of each month (UTC).",
  },
  {
    q: "Why charge by usage instead of locking features?",
    a: "Every plan should let you complete a full job application end-to-end. Locking video interviews or autofill behind the top tier would kill the magic for everyone else. Charging by volume keeps pricing honest and matches our real cost (AI tokens, transcription, agent runs).",
  },
  {
    q: "Can I switch plans later?",
    a: "Yes. Upgrade, downgrade, or cancel anytime from your account settings. Changes take effect at the start of your next billing cycle.",
  },
  {
    q: "What happens to my data if I downgrade?",
    a: "Your data is preserved — applications, profile, and history all stay intact. Your monthly quotas just match the new plan starting next cycle.",
  },
  {
    q: "Do you offer student discounts?",
    a: "Yes — students with a valid .edu email get 50% off Pro and Premium Agentic. Reach out from your student email to claim the discount.",
  },
];

function PricingPage() {
  const [cycle, setCycle] = useState<"monthly" | "yearly">("monthly");
  const navigate = useNavigate();
  const { user } = useAuth();
  const { subscription, isActive } = useSubscription();
  const { openCheckout, loading: checkoutLoading } = useStripeCheckout();

  // Resume pending checkout after auth (set on pricing-page CTA → signup flow)
  useMemo(() => {
    if (!user) return;
    try {
      const raw = sessionStorage.getItem("pending_checkout");
      if (!raw) return;
      const { priceId } = JSON.parse(raw) as { priceId?: string };
      sessionStorage.removeItem("pending_checkout");
      if (priceId) {
        openCheckout({ priceId }).catch(() => {});
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const LAUNCH_DISCOUNT = 0.25; // 25% off launch promo

  const priceFor = useMemo(
    () => (monthly: number) => {
      if (monthly === 0) return { current: 0, original: 0 };
      const base = cycle === "yearly" ? Math.round(monthly * 0.8) : monthly;
      const discounted = Math.round(base * (1 - LAUNCH_DISCOUNT) * 100) / 100;
      return { current: discounted, original: base };
    },
    [cycle],
  );

  const formatPrice = (n: number) =>
    Number.isInteger(n) ? `$${n}` : `$${n.toFixed(2)}`;

  const handleCta = async (tier: Tier) => {
    if (!tier.productId) {
      navigate({ to: user ? "/app/profile" : "/signup" });
      return;
    }
    if (!user) {
      try {
        sessionStorage.setItem(
          "pending_checkout",
          JSON.stringify({ priceId: cycle === "yearly" ? tier.yearlyPriceId : tier.monthlyPriceId }),
        );
      } catch {
        /* ignore */
      }
      navigate({ to: "/signup" });
      return;
    }
    if (isActive && subscription?.product_id === tier.productId) {
      toast.info("You're already on this plan.");
      navigate({ to: "/app/billing" });
      return;
    }
    // Existing subscriber switching plans → send to Billing for the Stripe
    // customer portal (handles proration). Avoids duplicate subscriptions.
    if (isActive && subscription?.product_id && subscription.product_id !== tier.productId) {
      toast.info("Switch plans from your billing page — we'll prorate the change.");
      navigate({ to: "/app/billing" });
      return;
    }
    const priceId = cycle === "yearly" ? tier.yearlyPriceId : tier.monthlyPriceId;
    if (!priceId) {
      toast.error("This plan isn't available right now.");
      return;
    }
    try {
      await openCheckout({ priceId });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not open checkout.");
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      {/* Top bar */}
      <Box
        sx={{
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Container maxWidth="lg">
          <Stack
            direction="row"
            sx={{ alignItems: "center", justifyContent: "space-between", py: 2 }}
          >
            <Stack
              direction="row"
              spacing={1}
              component={Link}
              to="/"
              sx={{ alignItems: "center", textDecoration: "none", color: "inherit" }}
            >
              <Box
                component="img"
                src={logo}
                alt="JobYoda logo"
                sx={{ width: 32, height: 32, borderRadius: 1.5, objectFit: "contain" }}
              />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                JobYoda
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button component={Link} to="/" color="inherit">
                Back to home
              </Button>
              <Button component={Link} to="/signup" variant="contained">
                Get started
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>

      <Container component="main" maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        {/* Header */}
        <Box sx={{ textAlign: "center", maxWidth: 760, mx: "auto", mb: { xs: 4, md: 6 } }}>
          <Chip label="Pricing" color="primary" variant="outlined" sx={{ mb: 2 }} />
          <Typography variant="h3" component="h1" sx={{ mb: 2, fontSize: { xs: 32, md: 44 } }}>
            Every feature on every plan.<br />Pay for the volume you need.
          </Typography>
          <Typography variant="body1" color="text.secondary">
            No locked features — Free users get to try tailored resumes, interview coaching, and the
            agent. Upgrade when you outgrow the monthly quotas.
          </Typography>

          <Stack direction="row" sx={{ justifyContent: "center", alignItems: "center", gap: 1.5, mt: 4 }}>
            <ToggleButtonGroup
              value={cycle}
              exclusive
              onChange={(_, v) => v && setCycle(v)}
              size="small"
              color="primary"
            >
              <ToggleButton value="monthly">Monthly</ToggleButton>
              <ToggleButton value="yearly">Yearly</ToggleButton>
            </ToggleButtonGroup>
            {cycle === "yearly" && (
              <Chip label="Save 20%" color="primary" size="small" sx={{ fontWeight: 600 }} />
            )}
          </Stack>
          <Stack direction="row" sx={{ justifyContent: "center", mt: 2 }}>
            <Chip
              label="🎉 Launch offer — 25% off all paid plans"
              color="secondary"
              sx={{ fontWeight: 700, letterSpacing: 0.3 }}
            />
          </Stack>
        </Box>

        {/* Tier price cards */}
        <Box
          sx={{
            display: "grid",
            gap: 3,
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" },
            alignItems: "stretch",
            mb: { xs: 6, md: 8 },
          }}
        >
          {TIERS.map((tier) => {
            const price = priceFor(tier.monthly);
            const isPremium = !!tier.recommended;
            return (
              <Card
                key={tier.name}
                elevation={isPremium ? 8 : 1}
                sx={{
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  border: isPremium ? "2px solid" : "1px solid",
                  borderColor: isPremium ? "primary.main" : "divider",
                  borderRadius: 3,
                  overflow: "visible",
                  transform: { md: isPremium ? "translateY(-12px)" : "none" },
                  background: isPremium
                    ? "linear-gradient(180deg, color-mix(in oklab, var(--mui-palette-primary-main) 8%, transparent) 0%, transparent 40%)"
                    : undefined,
                }}
              >
                {isPremium && (
                  <Chip
                    label="RECOMMENDED"
                    color="primary"
                    size="small"
                    sx={{
                      position: "absolute",
                      top: -12,
                      right: 16,
                      fontWeight: 700,
                      letterSpacing: 0.5,
                    }}
                  />
                )}

                <CardContent sx={{ flex: 1, p: 3 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {tier.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 56 }}>
                    {tier.tagline}
                  </Typography>

                  <Stack direction="row" sx={{ alignItems: "baseline", gap: 1, mb: 0.5, flexWrap: "wrap" }}>
                    {price.original > 0 && price.original !== price.current && (
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: "text.disabled",
                          textDecoration: "line-through",
                        }}
                      >
                        {formatPrice(price.original)}
                      </Typography>
                    )}
                    <Typography
                      variant="h3"
                      sx={{ fontWeight: 800, color: isPremium ? "primary.main" : "text.primary" }}
                    >
                      {formatPrice(price.current)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      /mo{cycle === "yearly" && tier.monthly > 0 ? " · billed yearly" : ""}
                    </Typography>
                  </Stack>
                  {price.original > 0 && price.original !== price.current && (
                    <Typography variant="caption" sx={{ color: "primary.main", fontWeight: 700, mb: 1.5, display: "block" }}>
                      25% off launch pricing
                    </Typography>
                  )}
                </CardContent>

                <CardActions sx={{ p: 3, pt: 0 }}>
                  <Button
                    fullWidth
                    size="large"
                    variant={tier.ctaVariant}
                    color="primary"
                    disabled={checkoutLoading}
                    onClick={() => handleCta(tier)}
                  >
                    {isActive && subscription?.product_id === tier.productId
                      ? "Current plan"
                      : tier.cta}
                  </Button>
                </CardActions>
              </Card>
            );
          })}
        </Box>

        {/* Quota comparison table */}
        <Box sx={{ mb: { xs: 8, md: 12 } }}>
          <Typography variant="h4" component="h2" sx={{ textAlign: "center", mb: 1 }}>
            What you get each month
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", mb: 4 }}>
            Quotas reset on the 1st of each month (UTC). Soft warning at 80%, hard cap at 100%.
          </Typography>

          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ borderRadius: 3, overflow: "auto" }}
          >
            <Table size="small" sx={{ minWidth: 720 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: "background.default" }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: 14, minWidth: 220 }}>
                    Capability
                  </TableCell>
                  {TIERS.map((t) => (
                    <TableCell
                      key={t.name}
                      align="center"
                      sx={{
                        fontWeight: 700,
                        fontSize: 14,
                        color: t.recommended ? "primary.main" : "text.primary",
                      }}
                    >
                      {t.name}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {QUOTAS.flatMap((section) => [
                  <TableRow key={`${section.section}-header`}>
                    <TableCell
                      colSpan={5}
                      sx={{
                        bgcolor: "action.hover",
                        fontWeight: 700,
                        fontSize: 12,
                        letterSpacing: 0.8,
                        textTransform: "uppercase",
                        color: "text.secondary",
                        py: 1,
                      }}
                    >
                      {section.section}
                    </TableCell>
                  </TableRow>,
                  ...section.rows.map((row) => (
                    <TableRow key={`${section.section}-${row.capability}`} hover>
                      <TableCell sx={{ verticalAlign: "top" }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {row.capability}
                        </Typography>
                        {row.hint && (
                          <Typography variant="caption" color="text.secondary">
                            {row.hint}
                          </Typography>
                        )}
                      </TableCell>
                      {row.values.map((v, i) => {
                        const isPremiumCol = i === 3;
                        const isUnlimited = v === "Unlimited";
                        return (
                          <TableCell
                            key={i}
                            align="center"
                            sx={{
                              fontWeight: isUnlimited || isPremiumCol ? 600 : 400,
                              color: isUnlimited
                                ? "primary.main"
                                : v === "0" || v === "Manual only"
                                ? "text.disabled"
                                : "text.primary",
                              fontSize: 14,
                            }}
                          >
                            {v}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  )),
                ])}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2, textAlign: "center" }}>
            Need more headroom on Pro? A top-up pack ($5 for +20 resumes or +50 Q&A answers) is coming soon.
          </Typography>
        </Box>

        <Divider sx={{ mb: { xs: 6, md: 8 } }} />

        {/* FAQ */}
        <Box sx={{ maxWidth: 760, mx: "auto" }}>
          <Typography variant="h4" component="h2" sx={{ textAlign: "center", mb: 3 }}>
            Frequently asked questions
          </Typography>
          {FAQS.map((item) => (
            <Accordion key={item.q} disableGutters elevation={0} sx={{ border: "1px solid", borderColor: "divider", "&:before": { display: "none" }, mb: 1, borderRadius: 2, overflow: "hidden" }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 600 }}>{item.q}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary">
                  {item.a}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </Container>
    </Box>
  );
}
